/**
 * Custom Validations (CV-4, LIQ-2271): a rule's criteria as a plain-English sentence, for the rules
 * table - e.g. "Refer to Underwriters must not be empty and must be Yes or No". Shared so the rule
 * builder (CV-5) can show the same wording while a rule is being built.
 *
 * Criteria follow criteria format v1 (CRITERIA_FORMAT.md in lincoln, criteria.py in
 * bdx-validation-service): { schema_version: 1, condition } where a condition is and / or / not / if,
 * a compare, or is_null / is_not_null. A rule's criteria say what a row must satisfy to PASS, so the
 * sentence says what "must" be true; the "if" part of a condition reads as a plain statement ("is").
 */

const COMPARE_WORDS = {
	"=": ["must be", "is"],
	"!=": ["must not be", "is not"],
	"<": ["must be less than", "is less than"],
	"<=": ["must be at most", "is at most"],
	">": ["must be more than", "is more than"],
	">=": ["must be at least", "is at least"],
	starts_with: ["must start with", "starts with"],
	ends_with: ["must end with", "ends with"],
	contains: ["must contain", "contains"],
};

const DATE_COMPARE_WORDS = {
	"<": ["must be before", "is before"],
	"<=": ["must be on or before", "is on or before"],
	">": ["must be after", "is after"],
	">=": ["must be on or after", "is on or after"],
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * A BDX column's name as people read it: "Refer_to_Underwriters" -> "Refer to Underwriters".
 * @param name the column name as a rule stores it
 */
export function columnLabel(name) {
	return String(name).replace(/_/g, " ").replace(/\s+/g, " ").trim();
}

/**
 * The sentence for a rule's criteria.
 * @param document the rule's criteria document
 * @param [options.columnTypes] column name -> "text" | "numeric" | "date" | "boolean" (GET
 *   /api/custom-validations/columns), so dates read "before" / "after" rather than "less" / "more"
 * @returns the sentence, or null when there are no criteria or they can't be read
 */
export function criteriaText(document, { columnTypes = {} } = {}) {
	if (!document || typeof document !== "object" || !document.condition) {
		return null;
	}
	try {
		const sentence = requirement(document.condition, { columnTypes });
		return sentence.charAt(0).toUpperCase() + sentence.slice(1);
	} catch (error) {
		return null;
	}
}

// "A, B or C" / "A and B".
function listText(items, joiner) {
	if (items.length <= 1) {
		return items.join("");
	}
	return `${items.slice(0, -1).join(", ")} ${joiner} ${items[items.length - 1]}`;
}

function formatDate(value) {
	const [year, month, day] = String(value).split("-").map(Number);
	return year && month && day ? `${day} ${MONTHS[month - 1]} ${year}` : String(value);
}

function literal(value) {
	if (typeof value === "boolean") {
		return value ? "true" : "false";
	}
	return value === "" ? "empty text" : String(value);
}

function isArithmetic(op) {
	return op && typeof op === "object" && Array.isArray(op.operands);
}

function operand(op, ctx) {
	if ("column" in op) {
		return columnLabel(op.column);
	}
	if ("text" in op) {
		return literal(op.text);
	}
	if ("number" in op) {
		return String(op.number);
	}
	if ("date" in op) {
		return formatDate(op.date);
	}
	if ("boolean" in op) {
		return literal(op.boolean);
	}
	if ("list" in op) {
		return listText(op.list.map(literal), "or");
	}
	if (isArithmetic(op)) {
		// A nested sum or product is bracketed, so "(A + B) * C" can't read as "A + B * C".
		return op.operands
			.map((inner) => (isArithmetic(inner) && inner.op !== op.op ? `(${operand(inner, ctx)})` : operand(inner, ctx)))
			.join(` ${op.op} `);
	}
	throw new Error("unknown operand");
}

function isDate(op, ctx) {
	return Boolean(op) && ("date" in op || ("column" in op && ctx.columnTypes[op.column] === "date"));
}

// A condition about one operand: { subject, must, is, are } - its predicate as a requirement, as a
// statement, and as a statement about several subjects ("are not empty"). null for and / or / not / if.
function simple(condition, ctx) {
	if (condition.type === "is_null" || condition.type === "is_not_null") {
		const not = condition.type === "is_not_null" ? "not " : "";
		return {
			subject: operand(condition.operand, ctx),
			must: `must ${not}be empty`,
			is: `is ${not}empty`,
			are: `are ${not}empty`,
		};
	}
	if (condition.type !== "compare") {
		return null;
	}
	const { op, left, right } = condition;
	let words = COMPARE_WORDS[op];
	let value = right && operand(right, ctx);
	if (op === "in" || op === "not_in") {
		const values = (right.list || []).map(literal);
		const many = values.length > 2;
		words = op === "in" ? ["must be", "is"] : ["must not be", "is not"];
		value = `${many ? (op === "in" ? "one of " : "any of ") : ""}${listText(values, "or")}`;
	} else if (DATE_COMPARE_WORDS[op] && (isDate(left, ctx) || isDate(right, ctx))) {
		words = DATE_COMPARE_WORDS[op];
	} else if ((op === "=" || op === "!=") && (isArithmetic(left) || isArithmetic(right))) {
		words = op === "=" ? ["must equal", "equals"] : ["must not equal", "doesn't equal"];
	} else if (op === "contains" && condition.whole_word) {
		words = ["must contain the word", "contains the word"];
	}
	if (!words) {
		throw new Error("unknown comparison");
	}
	const within = condition.tolerance ? ` (within ${condition.tolerance})` : "";
	return {
		subject: operand(left, ctx),
		must: `${words[0]} ${value}${within}`,
		is: `${words[1]} ${value}${within}`,
		are: null,
	};
}

// Joins the parts of an and / or: one subject said once ("X must not be empty and must be Yes or
// No"), subjects sharing a predicate said once ("A, B and C are not empty"), or whole clauses -
// bracketing a nested and / or of the other kind, and separating "if" rules with a semicolon.
function joined(condition, ctx, mood) {
	const joiner = condition.type === "and" ? "and" : "or";
	const parts = condition.conditions.map((inner) => simple(inner, ctx));
	if (parts.every(Boolean)) {
		if (parts.every((part) => part.subject === parts[0].subject)) {
			return `${parts[0].subject} ${listText(parts.map((part) => part[mood]), joiner)}`;
		}
		if (mood === "is") {
			const groups = [];
			parts.forEach((part) => {
				const group = groups.find((existing) => existing.is === part.is);
				if (group) {
					group.subjects.push(part.subject);
				} else {
					groups.push({ ...part, subjects: [part.subject] });
				}
			});
			if (groups.length < parts.length) {
				return listText(
					groups.map((group) => {
						if (group.subjects.length === 1) {
							return `${group.subject} ${group.is}`;
						}
						const verb = joiner === "and" && group.are ? group.are : group.is;
						return `${listText(group.subjects, joiner)} ${verb}`;
					}),
					joiner
				);
			}
		}
	}
	const clause = mood === "must" ? requirement : statement;
	const texts = condition.conditions.map((inner) => {
		const text = clause(inner, ctx);
		const other = (inner.type === "and" || inner.type === "or") && inner.type !== condition.type;
		return other ? `(${text})` : text;
	});
	if (joiner === "and" && mood === "must" && condition.conditions.some((inner) => inner.type === "if")) {
		return texts.join("; ");
	}
	return listText(texts, joiner);
}

// The opposite of a simple condition, when it has one: empty / not empty, = / !=, in / not in.
function negated(condition) {
	const opposite = { is_null: "is_not_null", is_not_null: "is_null" }[condition.type];
	if (opposite) {
		return { ...condition, type: opposite };
	}
	const op = { "=": "!=", "!=": "=", in: "not_in", not_in: "in" }[condition.type === "compare" ? condition.op : ""];
	return op ? { ...condition, op } : null;
}

// The condition as something that must be true.
function requirement(condition, ctx) {
	switch (condition.type) {
		case "and":
		case "or":
			return joined(condition, ctx, "must");
		case "not": {
			const opposite = negated(condition.condition);
			return opposite
				? requirement(opposite, ctx)
				: `it must not be the case that ${statement(condition.condition, ctx)}`;
		}
		case "if":
			return `if ${statement(condition.if, ctx)}, ${requirement(condition.then, ctx)}`;
		default: {
			const part = simple(condition, ctx);
			if (!part) {
				throw new Error("unknown condition");
			}
			return `${part.subject} ${part.must}`;
		}
	}
}

// The condition as a plain statement, for the "if" part of a rule.
function statement(condition, ctx) {
	switch (condition.type) {
		case "and":
		case "or":
			return joined(condition, ctx, "is");
		case "not": {
			const opposite = negated(condition.condition);
			return opposite
				? statement(opposite, ctx)
				: `it is not the case that ${statement(condition.condition, ctx)}`;
		}
		case "if":
			return `(if ${statement(condition.if, ctx)}, ${statement(condition.then, ctx)})`;
		default: {
			const part = simple(condition, ctx);
			if (!part) {
				throw new Error("unknown condition");
			}
			return `${part.subject} ${part.is}`;
		}
	}
}
